import { permissionGrpcToLiteral } from "@/gen_src/grpc_permissions";
import { PermissionLiteral } from "@/gen_src/permissions";
import {
  Permission,
  additional_permissions_list as additionalPermissionsListOption,
  permission as permissionOption,
  team_permission as teamPermissionOption,
} from "@/gen_src/proto/chalk/auth/v1/permissions_pb";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useIsImpersonating } from "@/utils/hooks/use-is-impersonating";
import { pluralize } from "@/utils/string-utils";
import { TeamRole, isSpecificProtoPermission } from "@/utils/teamService";
import type { DescMethod } from "@bufbuild/protobuf";
import { getOption } from "@bufbuild/protobuf";
import { isArray } from "lodash";
import { useSession } from "next-auth/react";
import { useMemo } from "react";

interface HasPermissionsOpts {
  allowAdminOverride?: boolean;
}

function useImpersonationPermissionOverride(): boolean {
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  const isImpersonating = useIsImpersonating({ cookieOnly: true });
  // Backend permission checks grant impersonating employees all permissions, independent of admin mode.
  return isChalkEmployee && isImpersonating;
}

export interface HasPermissionInfo {
  /**
   * Whether the team object has been loaded yet, indicating that the permissions boolean is accurate
   */
  loaded: boolean;
  /**
   * Whether the user has the given permission, taking into account the admin override
   */
  hasPermission: boolean;
  /**
   * Whether the user has the given permission, ignoring the admin override
   */
  actuallyHasPermission: boolean;
}

export interface HasPermissionForEndpointPermissionsArgs {
  envPermission: Permission;
  teamPermission: Permission;
  // Additional env permissions accepted in place of envPermission. The
  // effective acceptable set is the union of envPermission and this list;
  // a user holding any single permission in the union passes.
  // See `additional_permissions_list` MethodOption in permissions.proto.
  additionalPermissionsList: Permission[];
  userTeamPermissions: ReadonlySet<Permission>;
  userEnvPermissions: ReadonlySet<Permission>;
}

export interface HasPermissionForEndpointArgs {
  method: DescMethod;
  userTeamPermissions: ReadonlySet<Permission>;
  userEnvPermissions: ReadonlySet<Permission>;
}

export function getPermissionsForEndpoint(method: DescMethod): {
  teamPermission: Permission;
  envPermission: Permission;
  additionalPermissionsList: Permission[];
} {
  const envPermission = getOption(method, permissionOption);
  const teamPermission = getOption(method, teamPermissionOption);
  const additionalPermissionsList = getOption(method, additionalPermissionsListOption);

  return {
    envPermission: envPermission,
    teamPermission: teamPermission,
    additionalPermissionsList: additionalPermissionsList,
  };
}

function userHoldsEnvPermission(
  permission: Permission,
  userEnvPermissions: ReadonlySet<Permission>,
  userTeamPermissions: ReadonlySet<Permission>,
): boolean {
  return userEnvPermissions.has(permission) || userTeamPermissions.has(permission);
}

export function getActuallyHasPermissionForEndpointPermissions({
  envPermission,
  teamPermission,
  additionalPermissionsList,
  userTeamPermissions,
  userEnvPermissions,
}: HasPermissionForEndpointPermissionsArgs): boolean {
  const hasRequiredTeamPermission =
    !isSpecificProtoPermission(teamPermission) || userTeamPermissions.has(teamPermission);

  // Union semantics: build the set of acceptable permissions (envPermission +
  // additionalPermissionsList) and accept if the user holds any one of them.
  const acceptablePermissions: Permission[] = [];
  if (isSpecificProtoPermission(envPermission)) {
    acceptablePermissions.push(envPermission);
  }
  acceptablePermissions.push(...additionalPermissionsList);

  const hasRequiredEnvPermission =
    // If neither field constrains, the endpoint imposes no env-level requirement.
    (acceptablePermissions.length === 0 && !isSpecificProtoPermission(envPermission)) ||
    acceptablePermissions.some((p) =>
      userHoldsEnvPermission(p, userEnvPermissions, userTeamPermissions),
    );

  return hasRequiredTeamPermission && hasRequiredEnvPermission;
}

export function getActuallyHasPermissionForEndpoint({
  method,
  userTeamPermissions,
  userEnvPermissions,
}: HasPermissionForEndpointArgs): boolean {
  const { envPermission, teamPermission, additionalPermissionsList } =
    getPermissionsForEndpoint(method);
  return getActuallyHasPermissionForEndpointPermissions({
    envPermission,
    teamPermission,
    additionalPermissionsList,
    userTeamPermissions,
    userEnvPermissions,
  });
}

export function useHasPermissionForEndpoint(
  method: DescMethod,
  { allowAdminOverride = true }: HasPermissionsOpts = {},
): HasPermissionInfo {
  const { teamService, environmentId, team } = useRouteAndTeamInfo();
  const session = useSession();
  const impersonationOverride = useImpersonationPermissionOverride();

  const userTeamPermissions =
    session.data != null
      ? teamService.protoPermissionsForUserTeamScoped(session.data.userId)
      : new Set<Permission>();

  const userEnvPermissions =
    session.data != null && environmentId != null
      ? teamService.protoPermissionsForUserEnv(environmentId, session.data.userId)
      : new Set<Permission>();

  const actuallyHasPermission = getActuallyHasPermissionForEndpoint({
    method,
    userTeamPermissions,
    userEnvPermissions,
  });

  return {
    actuallyHasPermission,
    hasPermission: actuallyHasPermission || (allowAdminOverride && impersonationOverride),
    loaded: team != null,
  };
}

export function useHasPermission(
  permissionId: PermissionLiteral | PermissionLiteral[],
  { allowAdminOverride = true }: HasPermissionsOpts = {},
): HasPermissionInfo {
  const { teamService, environmentId, team } = useRouteAndTeamInfo();
  const session = useSession();
  const impersonationOverride = useImpersonationPermissionOverride();

  const teamLevelPerms = useHasTeamScopedPermission(permissionId, {
    allowAdminOverride: allowAdminOverride,
  });

  const permissionsForUserEnv =
    session.data != null && environmentId != null
      ? teamService.permissionsForUserEnv(environmentId, session.data.userId)
      : new Set<string>();

  const hasPermissionForEnvironment = isArray(permissionId)
    ? permissionId.every((p) => permissionsForUserEnv.has(p))
    : permissionsForUserEnv.has(permissionId);

  const hasPermissionForEnvironmentConsideringOverride =
    (allowAdminOverride && impersonationOverride) || hasPermissionForEnvironment;

  return {
    actuallyHasPermission: hasPermissionForEnvironment || teamLevelPerms.actuallyHasPermission,
    hasPermission: hasPermissionForEnvironmentConsideringOverride || teamLevelPerms.hasPermission,
    loaded: team != null,
  };
}

export function useHasTeamScopedPermission(
  permissionId: PermissionLiteral | PermissionLiteral[],
  { allowAdminOverride = true }: HasPermissionsOpts = {},
): HasPermissionInfo {
  const { teamService, team } = useRouteAndTeamInfo();
  const session = useSession();
  const impersonationOverride = useImpersonationPermissionOverride();

  const teamScopedPermissionsForUser =
    session.data != null
      ? teamService.permissionsForUserTeamScoped(session.data.userId)
      : new Set<PermissionLiteral>();

  const hasPermission = isArray(permissionId)
    ? permissionId.every((p) => teamScopedPermissionsForUser.has(p))
    : teamScopedPermissionsForUser.has(permissionId);

  const hasPermissionConsideringOverride =
    (allowAdminOverride && impersonationOverride) || hasPermission;

  return {
    actuallyHasPermission: hasPermission,
    hasPermission: hasPermissionConsideringOverride,
    loaded: team != null,
  };
}

export function useAuthRoles(teamLevel?: boolean): TeamRole[] {
  const { teamService, environmentId } = useRouteAndTeamInfo();
  const session = useSession();

  return useMemo(() => {
    if (!session.data?.userId) {
      return [];
    }

    if (teamLevel) {
      const teamRole = teamService.individualRoleForUserTeamScoped(session.data.userId);
      return teamRole ? [teamRole] : [];
    }

    if (!environmentId) {
      return [];
    }

    return teamService.allRolesForUserForEnvironment(environmentId, session.data.userId);
  }, [session, teamService, environmentId, teamLevel]);
}

export function useFullAuthRolesForUser(): {
  teamRole: TeamRole | undefined;
  envRoles: TeamRole[];
  hasSelectedEnvironment: boolean;
} {
  const { teamService, environmentId } = useRouteAndTeamInfo();
  const session = useSession();

  return useMemo(() => {
    if (!session.data?.userId) {
      return { teamRole: undefined, envRoles: [], hasSelectedEnvironment: environmentId != null };
    }

    const teamRole = teamService.individualRoleForUserTeamScoped(session.data.userId);
    const envRoles =
      environmentId != null
        ? teamService.allRolesForUserForEnvironment(environmentId, session.data.userId)
        : [];

    return { teamRole, envRoles, hasSelectedEnvironment: environmentId != null };
  }, [session, teamService, environmentId]);
}

/**
 * Given a permission or list of permissions, returns the roles that have that permission.
 * This allows you to say "given these actions that require these permissions, what roles
 * might you need to perform them?"
 *
 * Please memoize your input if it is an array.
 */
export function useRequiredRoles(
  permissionId: PermissionLiteral | PermissionLiteral[],
): TeamRole[] {
  const { teamService } = useRouteAndTeamInfo();
  return (
    teamService.rolesWithPermissions(isArray(permissionId) ? permissionId : [permissionId]) ?? []
  );
}

/**
 * Returns a list of roles which would give the user the provided permissions. Any of the roles would work,
 * i.e. "required roles" means "one of these roles is required".
 */
export function useRequiredRolesProto(permissionId: Permission[] | Permission): TeamRole[] {
  const { teamService } = useRouteAndTeamInfo();
  return (
    teamService.rolesWithPermissionsProto(isArray(permissionId) ? permissionId : [permissionId]) ??
    []
  );
}

export interface UseGetPermissionWarningArgs {
  actionName?: string;
  requiredPermissions: PermissionLiteral[];
  teamLevel?: boolean;
}

export interface UseGetPermissionWarningForEndpointArgs {
  actionName?: string;
  method: DescMethod;
}

export function getPermissionWarning({
  actionName = "This action",
  requiredPermissions,
  role,
  teamLevel,
  requiredRoles,
}: {
  actionName?: string;
  requiredPermissions: PermissionLiteral[];
  role: TeamRole;
  teamLevel?: boolean;
  requiredRoles: TeamRole[];
}): string {
  const yourRoleText = teamLevel
    ? role?.name ?? "[no team role]"
    : role?.name ?? "Viewer (default)";

  const requiredRolesText =
    requiredRoles.length > 0
      ? requiredRoles.map((role) => `'${role.name}'`).join(", ")
      : "[no default roles found with this permission, a custom role may be required]";

  return `${actionName} requires ${teamLevel ? "team-level " : ""}${pluralize(requiredPermissions.length, "the permission", "permissions")}: 
   ${requiredPermissions.map((perm) => `'${perm}'`).join(", ")}. You have the role 
   '${yourRoleText}', but you need one of the following roles ${teamLevel ? "at the team level" : ""}:  
   ${requiredRolesText}.`;
}

export function useGetPermissionWarning({
  actionName = "This action",
  requiredPermissions,
  teamLevel,
}: UseGetPermissionWarningArgs): string {
  const roles = useAuthRoles(teamLevel);
  const role = roles[0];
  const requiredRoles = useRequiredRoles(requiredPermissions);

  return getPermissionWarning({
    actionName,
    requiredPermissions,
    role,
    teamLevel,
    requiredRoles,
  });
}

export function getRequiredRolesForActionMessage({
  actionName = "This action",
  requiredTeamRoles,
  requiredEnvRoles,
  requiredTeamPermission,
  requiredEnvPermission,
  additionalPermissionsList = [],
}: {
  actionName: string;
  requiredTeamRoles: TeamRole[];
  requiredEnvRoles: TeamRole[];
  requiredTeamPermission: Permission;
  requiredEnvPermission: Permission;
  additionalPermissionsList?: Permission[];
}): string {
  if (isSpecificProtoPermission(requiredTeamPermission)) {
    const requiredRolesText = requiredTeamRoles.length
      ? `[${requiredTeamRoles.map((role) => `'${role.name}'`).join(", ")}]`
      : "[no default roles found with this permission, a custom role may be required]";

    return `${actionName} requires the team-level permission '${permissionGrpcToLiteral(requiredTeamPermission)}', which is granted by one of the following roles: ${requiredRolesText}.`;
  }

  // Build the full set of acceptable env permissions: singular + additional.
  const acceptableEnvPermissions: Permission[] = [];
  if (isSpecificProtoPermission(requiredEnvPermission)) {
    acceptableEnvPermissions.push(requiredEnvPermission);
  }
  for (const p of additionalPermissionsList) {
    if (isSpecificProtoPermission(p)) {
      acceptableEnvPermissions.push(p);
    }
  }

  if (acceptableEnvPermissions.length > 0) {
    const requiredRolesText = requiredEnvRoles.length
      ? `[${requiredEnvRoles.map((role) => `'${role.name}'`).join(", ")}]`
      : "[no default roles found with this permission, a custom role may be required]";

    if (acceptableEnvPermissions.length === 1) {
      return `${actionName} requires the permission '${permissionGrpcToLiteral(
        acceptableEnvPermissions[0],
      )}', which is granted by one of the following roles: ${requiredRolesText}.`;
    }

    const permissionsList = acceptableEnvPermissions
      .map((p) => `'${permissionGrpcToLiteral(p)}'`)
      .join(" or ");
    return `${actionName} requires one of the permissions [${permissionsList}], any of which is granted by one of the following roles: ${requiredRolesText}.`;
  }

  return `${actionName} does not require any specific permission.`;
}

export function getUserRolesMessage(
  userTeamRole: TeamRole | undefined,
  userEnvRole: TeamRole | undefined,
  hasSelectedEnvironment: boolean,
): string {
  if (userEnvRole) {
    if (userTeamRole) {
      return `You have the team-level role '${userTeamRole.name}' and the environment-level role '${userEnvRole.name}'.`;
    }
    return `You have the default Viewer role at the team level and the environment-level role '${userEnvRole.name}'.`;
  }

  if (hasSelectedEnvironment) {
    if (userTeamRole) {
      return `You have the team-level role '${userTeamRole.name}' and the default Viewer role for this environment.`;
    }
    return "You have the default Viewer role at the team level and for this environment.";
  }

  if (userTeamRole) {
    return `You have the team-level role '${userTeamRole.name}' and no environment is selected.`;
  }

  return "You have the default Viewer role at the team level and no environment is selected.";
}

/**
 * For a given gRPC endpoint with a generic "action" alias, produce a message stating which permissions are required to perform that action,
 * which roles grant those permissions, and which roles the user currently has. This is useful for a blocked-permissions warning or a tooltip
 * explaining why a button is disabled.
 */
export function useGetPermissionWarningForEndpoint({
  actionName = "This action",
  method,
}: UseGetPermissionWarningForEndpointArgs): string {
  const {
    teamRole: userTeamRole,
    envRoles: userEnvRoles,
    hasSelectedEnvironment,
  } = useFullAuthRolesForUser();
  const { teamService } = useRouteAndTeamInfo();

  const {
    envPermission: requiredEnvPermission,
    teamPermission: requiredTeamPermission,
    additionalPermissionsList,
  } = getPermissionsForEndpoint(method);
  const requiredTeamRoles = useRequiredRolesProto(requiredTeamPermission);

  // For the env-level message we want every role that satisfies the OR-check
  // across singular envPermission + additional_permissions_list. Use the union
  // helper rather than rolesWithPermissionsProto (which returns the
  // intersection of roles holding ALL permissions).
  const requiredEnvRoles = useMemo(() => {
    const acceptable: Permission[] = [];
    if (isSpecificProtoPermission(requiredEnvPermission)) {
      acceptable.push(requiredEnvPermission);
    }
    for (const p of additionalPermissionsList) {
      if (isSpecificProtoPermission(p)) acceptable.push(p);
    }
    return teamService.rolesWithAnyPermissionProto(acceptable);
  }, [teamService, requiredEnvPermission, additionalPermissionsList]);

  const requiredRolesMessage = getRequiredRolesForActionMessage({
    actionName,
    requiredTeamRoles,
    requiredEnvRoles,
    requiredTeamPermission,
    requiredEnvPermission,
    additionalPermissionsList,
  });

  const userRolesMessage = getUserRolesMessage(
    userTeamRole,
    userEnvRoles[0],
    hasSelectedEnvironment,
  );

  return `${requiredRolesMessage} ${userRolesMessage}`;
}
